/**
 * Triggers configuration section for the POC-reassignment modal.
 * Supports api, cron, order_start, and after_oncomer trigger types.
 */

import { Button, Select, TextInput } from "risa-oasis-ui_v2";
import type { PocReassignmentTrigger } from "../../../data-model/pocReassignmentRule";
import { AddIcon } from "../../../svg/add-icon";
import { Delete } from "../../../svg/delete";
import { getTriggerTypeOptions } from "../shared/ruleFormConstants";

interface PocReassignmentTriggersSectionProps {
  triggers: PocReassignmentTrigger[];
  onAddTrigger: () => void;
  onRemoveTrigger: (index: number) => void;
  onTypeChange: (index: number, value: string) => void;
  onValueChange: (index: number, value: string) => void;
}

/** Renders the "Triggers" card with type and value fields for POC-reassignment triggers. */
const PocReassignmentTriggersSection = ({
  triggers,
  onAddTrigger,
  onRemoveTrigger,
  onTypeChange,
  onValueChange,
}: PocReassignmentTriggersSectionProps) => (
  <div className="flex flex-col gap-2 rounded-md border border-primaryGray-14 p-4">
    <div className="flex flex-row items-center justify-between">
      <div className="text-sm font-semibold">Triggers</div>
      <Button
        buttonType="tertiary"
        size="small"
        disabled={false}
        onClick={onAddTrigger}
      >
        <AddIcon width="14" height="14" color="#0056d6" />
        Add trigger
      </Button>
    </div>
    <div className="text-tiny text-primaryGray-8">
      Select a trigger type for each row. `api` fires on demand; `order_start`
      and `after_oncomer` fire during order processing.
    </div>
    {triggers.length === 0 ? (
      <div className="text-tiny text-primaryGray-12">
        No triggers configured.
      </div>
    ) : (
      triggers.map((trigger, index) => {
        const triggerValue = (trigger as { value?: string }).value ?? "";
        return (
          <div
            key={`trigger-${index}`}
            className="flex flex-row items-end gap-2"
          >
            <div className="w-40">
              <Select
                id={`poc-reassignment-trigger-type-${index}`}
                label={index === 0 ? "Type" : ""}
                options={getTriggerTypeOptions(trigger.type)}
                placeholder="Select type"
                value={trigger.type}
                onOptionChange={(data) => onTypeChange(index, data.value)}
              />
            </div>
            <div className="mb-1.5 flex-1">
              <TextInput
                id={`poc-reassignment-trigger-value-${index}`}
                label={index === 0 ? "Value" : ""}
                placeholder="Enter value"
                defaultValue={triggerValue}
                onChange={(d) => onValueChange(index, d.value ?? "")}
                onBlur={(d) => onValueChange(index, d.value ?? "")}
              />
            </div>
            <div
              className="mb-5 cursor-pointer"
              onClick={() => onRemoveTrigger(index)}
            >
              <Delete width={16} height={16} />
            </div>
          </div>
        );
      })
    )}
  </div>
);

export default PocReassignmentTriggersSection;
